import { memo, useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { useDelayHint } from "../../../core/utils/general";
import { LoadingIndicator, LoadingIndicatorProps } from "../../LoadingIndicator";
import { PageHeader, PageHeaderProps } from "../../PageHeader";

import styles from "./style.module.scss";

export type PageLayoutProps = PageHeaderProps & {
  stickyHeader?: boolean;
  pageLayoutContentClassName?: string;
  isFetching?: boolean;
  loaderProps?: LoadingIndicatorProps;
};

export const PageLayout = memo<PageLayoutProps>((
  {
    children,
    headerContainerClassname,
    stickyHeader = true,
    pageLayoutContentClassName,
    isFetching: externalIsFetching,
    actions,
    additionalActions,
    className,
    loaderProps,
    ...props
  }) => {
  const [ isSticky, setSticky ] = useState(false);
  const [ currentNode, setCurrentNode ] = useState<HTMLDivElement | null>(null);
  const ref = useCallback((ref: HTMLDivElement | null) => setCurrentNode(ref), []);

  useEffect(() => {
    if (currentNode != null && stickyHeader) {
      const animation = {
        frame: 0,
      };

      let node = currentNode as HTMLElement;
      const parentNodes = [] as HTMLElement[];
      while (node?.parentElement != null) {
        parentNodes.push(node.parentElement);
        node = node?.parentElement;
      }

      const handleScroll = (ev: Event) => {
        cancelAnimationFrame(animation.frame);
        animation.frame = requestAnimationFrame(time => {
          if (currentNode.getBoundingClientRect()) {
            setSticky((ev.target as HTMLElement).scrollTop > 0);
          }
        });
      };

      parentNodes.forEach(parent => parent?.addEventListener("scroll", handleScroll));
      return () => {
        cancelAnimationFrame(animation.frame);
        parentNodes.forEach(parent => parent?.removeEventListener("scroll", handleScroll));
      };
    }
  }, [ currentNode, stickyHeader ]);

  const isFetching = useDelayHint(!!externalIsFetching, 16, false);
  
  return (
    <PageHeader
      className={classNames(styles.root, className)}
      headerContainerClassname={classNames(styles.headerContainer, headerContainerClassname, { [styles.sticky]: stickyHeader })}
      divider={false}
      ref={ref}
      data-page-header-sticky={isSticky}
      {...props}
      breadcrumbsProps={{
        containerType: "text",
        dividerType: "chevron",
        ...props.breadcrumbsProps,
      }}
      actions={isFetching ? undefined : actions}
      additionalActions={isFetching ? undefined : additionalActions}
    >
      <div className={classNames(styles.content, pageLayoutContentClassName)}>
        {
          isFetching ?
            <LoadingIndicator
              loadingIndicatorSize={"md"}
              loadingIndicatorType={"lineSimple"}
              loadingIndicatorLabel={""}
              {...loaderProps}
              className={classNames(styles.loader, loaderProps?.className)}
            />
            : children
        }
      </div>
    </PageHeader>
  );
});